import { Link } from "react-router-dom";
import { Check, Zap, MapPin, Monitor, ArrowRight } from "lucide-react";
import { RUSH_TIMELINE } from "../data/content";
import { inr, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useCurrency } from "../context/CurrencyContext";

const CLIENT_SIDE = ["Explains requirement", "Gives feedback", "Tests features", "Requests changes"];
const SPECIALIST_SIDE = ["Designs", "Codes", "Integrates", "Tests", "Deploys"];
const SESSION_STAGES = ["Requirements", "Architecture", "Live development", "Testing", "Change requests", "Deployment", "Handover"];

export default function RushHour() {
  const { settings } = useSite();
  const { money } = useCurrency();
  const r = settings.rates;

  const pros = [
    {
      key: "specialist", name: "VIBE CODING SPECIALIST", rate: r.specialist,
      ideal: ["MVPs", "Dashboards", "Internal tools", "Portals", "Workflow applications", "Automation", "Prototypes"],
      cta: "BOOK SPECIALIST", beam: false,
    },
    {
      key: "super", name: "SUPER VIBE CODER", rate: r.super,
      ideal: ["Critical projects", "Enterprise prototypes", "Executive stakeholder sessions", "Complex MVPs", "High-priority applications", "Strategic builds"],
      cta: "BOOK SUPER VIBE CODER", beam: true,
    },
  ];

  return (
    <div data-testid="rush-hour-page">
      {/* Hero */}
      <section className="pt-32 lg:pt-44 pb-20 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.12]"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1599837565318-67429bde7162?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
        <div className="container-x relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-4 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse-dot" />
            <span className="font-mono text-[10px] font-bold tracking-[0.25em] text-red-400">LIVE BUILD SESSIONS</span>
          </div>
          <h1 className="mt-8 font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white max-w-4xl">
            BUILD YOUR APPLICATION.<br /><span className="text-flame">LIVE.</span>
          </h1>
          <p className="mt-7 text-slate-300 max-w-2xl leading-relaxed">
            Rush Hour is an accelerated development engagement where you and your stakeholders participate directly in
            the development session. Don't just get progress reports — watch the software being built.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/book-rush-hour" data-testid="rush-page-book-btn" onClick={() => track("rush_hour_click", { location: "rush_page_hero" })} className="inline-flex items-center justify-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold px-8 py-4 transition-colors shadow-[0_0_32px_rgba(249,115,22,0.4)]">
              <Zap className="w-4 h-4" /> REQUEST RUSH HOUR
            </Link>
            <div className="flex items-center gap-5 text-xs font-mono tracking-wider text-slate-400">
              <span className="flex items-center gap-2"><Monitor className="w-4 h-4 text-flame" /> REMOTE</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-flame" /> ON-SITE</span>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-slate-600">On-site sessions: travel and applicable logistics charges may be additional.</p>
        </div>
      </section>

      {/* Professionals */}
      <section className="py-20 border-t border-white/5">
        <div className="container-x">
          <p className="section-label">CHOOSE YOUR SPECIALIST</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {pros.map((p) => (
              <div key={p.key} data-testid={`rush-pro-${p.key}`} className={`rounded-2xl p-8 sm:p-12 card-lift ${p.beam ? "bg-navy-800 tracing-beam" : "border border-white/10 bg-navy-800"}`}>
                <h2 className="font-heading text-2xl font-extrabold text-white">{p.name}</h2>
                <p className="mt-5 font-heading text-5xl font-black text-white" data-testid={`rush-pro-rate-${p.key}`}>
                  {money(p.rate)}<span className="text-base font-semibold text-slate-400">/hour</span>
                </p>
                <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-slate-500">IDEAL FOR</p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                  {p.ideal.map((i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <Check className="w-3.5 h-3.5 text-flame shrink-0" /> {i}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book-rush-hour?specialist=${p.key}`}
                  data-testid={`rush-book-${p.key}-btn`}
                  onClick={() => track("rush_hour_click", { location: `pro_${p.key}` })}
                  className={`mt-9 inline-flex items-center gap-2 rounded-full font-heading font-bold text-sm tracking-wide px-7 py-3.5 transition-colors ${
                    p.beam
                      ? "bg-flame hover:bg-flame-hover text-white shadow-[0_0_28px_rgba(249,115,22,0.4)]"
                      : "border border-white/25 text-white hover:border-flame hover:text-flame"
                  }`}
                >
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500 max-w-3xl leading-relaxed">
            Rush Hour is suitable for clearly defined applications whose scope can reasonably fit within the selected
            session. Many suitable MVPs, prototypes, dashboards, portals and focused business applications may be
            completed in approximately 3–5 hours depending on scope. Sessions are confirmed after requirement review,
            availability confirmation and payment.
          </p>
        </div>
      </section>

      {/* Live build experience split */}
      <section className="py-20 border-t border-white/5 bg-navy-900/50">
        <div className="container-x">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
            DON'T JUST GET PROGRESS REPORTS. <span className="text-flame">WATCH THE SOFTWARE BEING BUILT.</span>
          </h2>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch">
            <div className="rounded-2xl border border-white/10 bg-navy-800 p-8">
              <p className="font-mono text-[11px] tracking-[0.25em] text-slate-400 mb-6">CLIENT</p>
              <ul className="space-y-4">
                {CLIENT_SIDE.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex lg:flex-col items-center justify-center gap-3 rounded-2xl border border-flame/40 bg-flame/10 px-8 py-6">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse-dot" />
              <p className="font-mono text-[11px] font-bold tracking-[0.3em] text-flame text-center">LIVE<br className="hidden lg:block" /> APPLICATION</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-navy-800 p-8">
              <p className="font-mono text-[11px] tracking-[0.25em] text-flame mb-6">BUILT2LAST SPECIALIST</p>
              <ul className="space-y-4">
                {SPECIALIST_SIDE.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-flame" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-12 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
            BUILD. TEST. CHANGE. DEPLOY. <span className="text-flame">ALL IN THE SAME SESSION.</span>
          </p>
        </div>
      </section>

      {/* Session contents + timeline */}
      <section className="py-20 border-t border-white/5">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <p className="section-label">WHAT A SESSION COVERS</p>
            <ul className="mt-8 space-y-3.5">
              {SESSION_STAGES.map((s, i) => (
                <li key={s} className="flex items-center gap-4 rounded-lg border border-white/5 bg-navy-800 px-5 py-4">
                  <span className="font-mono text-xs font-bold text-flame">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-semibold text-slate-200">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="section-label">ILLUSTRATIVE SESSION TIMELINE</p>
            <div className="mt-8 rounded-2xl border border-white/10 bg-navy-800 p-8">
              <ul className="space-y-0">
                {RUSH_TIMELINE.map((t, i) => (
                  <li key={t.time} className="flex items-center gap-5 relative">
                    <div className="flex flex-col items-center">
                      <span className={`w-3 h-3 rounded-full border-2 ${i === 2 ? "bg-flame border-flame animate-pulse-dot" : "bg-navy border-flame/60"}`} />
                      {i < RUSH_TIMELINE.length - 1 && <span className="w-px h-8 bg-white/10" />}
                    </div>
                    <div className="pb-8">
                      <p className="font-mono text-sm font-bold text-flame">{t.time}</p>
                      <p className="font-mono text-[10px] tracking-[0.2em] text-slate-400">{t.label}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-600 font-mono">Illustrative workflow. Actual duration depends on project scope and complexity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking workflow */}
      <section className="py-20 border-t border-white/5 bg-navy-900/50">
        <div className="container-x text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">HOW BOOKING WORKS</h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-y-3">
            {["REQUEST", "SCOPE REVIEW", "AVAILABILITY", "ESTIMATE", "PAYMENT", "CONFIRMED"].map((s, i, arr) => (
              <div key={s} className="flex items-center">
                <span className="rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.15em] text-slate-200">{s}</span>
                {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-flame mx-2 shrink-0" />}
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">Bookings are never auto-confirmed — every session is reviewed for scope and availability first.</p>
          <Link to="/book-rush-hour" data-testid="rush-page-bottom-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold px-9 py-4 transition-colors shadow-[0_0_32px_rgba(249,115,22,0.4)]">
            <Zap className="w-4 h-4" /> REQUEST RUSH HOUR
          </Link>
        </div>
      </section>
    </div>
  );
}
